import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  ChevronLeft, Check, ArrowDownToLine, ArrowUpFromLine,
  Truck, Banknote, Clock, AlertCircle, X, Ban,
} from 'lucide-react';
import { fmtMoney as fmt } from '../utils/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (isoStr) => {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: true });
};

export function MovimientoDinero() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initialTipo = location.state?.tipo || 'salida';
  const [tipo, setTipo] = useState(initialTipo);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Salida state
  const [puntosVenta, setPuntosVenta] = useState([]);
  const [destino, setDestino] = useState(null);
  const [monto, setMonto] = useState('');
  const [nota, setNota] = useState('');
  const [historialSalidas, setHistorialSalidas] = useState([]);

  // Entrada state
  const [pendientes, setPendientes] = useState([]);
  const [seleccionado, setSeleccionado] = useState(null);
  const [montoConfirmado, setMontoConfirmado] = useState('');
  const [historialEntradas, setHistorialEntradas] = useState([]);
  const [cancelando, setCancelando] = useState(null);
  const [rechazando, setRechazando] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [rechazado, setRechazado] = useState(false);

  useEffect(() => {
    loadData();
  }, [tipo]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      if (tipo === 'salida') {
        const [puntosRes, histRes] = await Promise.all([
          axios.get(`${API}/puntos-venta/estados`),
          axios.get(`${API}/transitos-dinero/historial-salidas/${user.punto_venta_id}`),
        ]);
        const allPuntos = puntosRes.data;
        const miPunto = allPuntos.find(p => p.id === user.punto_venta_id);
        const esMoto = miPunto?.categoria === 'moto';
        setPuntosVenta(allPuntos.filter(p => {
          if (p.id === user.punto_venta_id) return false;
          if (esMoto) return true;
          return p.categoria === 'moto';
        }));
        setHistorialSalidas(histRes.data);
      } else {
        const [pendRes, histRes] = await Promise.all([
          axios.get(`${API}/transitos-dinero/pendientes/${user.punto_venta_id}`),
          axios.get(`${API}/transitos-dinero/historial-entradas/${user.punto_venta_id}`),
        ]);
        setPendientes(pendRes.data);
        setHistorialEntradas(histRes.data);
      }
    } catch {
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleTipoChange = (t) => {
    setTipo(t);
    setDestino(null);
    setMonto('');
    setNota('');
    setSeleccionado(null);
    setMontoConfirmado('');
    setSuccess(false);
    setError('');
  };

  const selectPendiente = (t) => {
    setSeleccionado(t);
    setMontoConfirmado(String(t.monto));
  };

  const handleSubmitSalida = async () => {
    if (!destino) { setError('Selecciona el punto de venta destino'); return; }
    const montoNum = parseFloat(monto);
    if (!montoNum || montoNum <= 0) { setError('Ingresa un monto válido mayor a cero'); return; }
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/transitos-dinero`, {
        origen_id: user.punto_venta_id,
        destino_id: destino,
        monto: montoNum,
        nota: nota.trim() || undefined,
      });
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al registrar envío');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelarEnvio = async (id) => {
    try {
      await axios.delete(`${API}/transitos-dinero/${id}`);
      setCancelando(null);
      loadData();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al cancelar envío');
      setCancelando(null);
    }
  };

  const handleConfirmarEntrada = async () => {
    const montoNum = parseFloat(montoConfirmado);
    if (montoNum < 0 || isNaN(montoNum)) { setError('Ingresa un monto válido'); return; }
    setError('');
    setSubmitting(true);
    try {
      await axios.put(`${API}/transitos-dinero/${seleccionado.id}/confirmar`, {
        monto_confirmado: montoNum,
      });
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al confirmar recepción');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRechazarEntrada = async () => {
    if (motivoRechazo.trim().length < 3) { setError('Escribe el motivo del rechazo'); return; }
    setError('');
    setSubmitting(true);
    try {
      await axios.put(`${API}/transitos-dinero/${seleccionado.id}/rechazar`, { motivo: motivoRechazo.trim() });
      setRechazado(true);
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al rechazar el envío');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="inline-block w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (success && rechazado) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 gap-4">
        <div className="w-20 h-20 rounded-full flex items-center justify-center bg-red-100">
          <Ban className="w-10 h-10 text-red-600" />
        </div>
        <h1 className="text-2xl font-bold text-zinc-900">Envío de dinero rechazado</h1>
        <p className="text-4xl font-bold text-zinc-900">{fmt(seleccionado?.monto)}</p>
        <p className="text-zinc-500 text-center">No entra a tu caja. Quedará registrado en la bitácora y en tu corte.</p>
        <button
          onClick={() => navigate(location.state?.volverA || '/pos')}
          className="mt-4 px-8 h-12 bg-zinc-900 text-white rounded-xl font-medium"
        >
          {location.state?.volverA === '/corte' ? 'Volver al corte' : 'Volver al POS'}
        </button>
      </div>
    );
  }

  if (success) {
    const montoFmt = tipo === 'salida'
      ? fmt(parseFloat(monto))
      : fmt(parseFloat(montoConfirmado));
    const destinoNombre = puntosVenta.find(p => p.id === destino)?.name;
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 gap-4">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center ${tipo === 'entrada' ? 'bg-green-100' : 'bg-orange-100'}`}>
          <Banknote className={`w-10 h-10 ${tipo === 'entrada' ? 'text-green-600' : 'text-orange-600'}`} />
        </div>
        <h1 className="text-2xl font-bold text-zinc-900">
          {tipo === 'entrada' ? 'Entrada de dinero confirmada' : 'Envío de dinero registrado'}
        </h1>
        <p className="text-4xl font-bold text-zinc-900">{montoFmt}</p>
        <p className="text-zinc-500 text-center">
          {tipo === 'salida'
            ? `En tránsito hacia ${destinoNombre}`
            : `Recibido de ${seleccionado?.origen_nombre}`}
        </p>
        <button
          onClick={() => navigate(location.state?.volverA || '/pos')}
          className="mt-4 px-8 h-12 bg-zinc-900 text-white rounded-xl font-medium"
        >
          {location.state?.volverA === '/corte' ? 'Volver al corte' : 'Volver al POS'}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="movimiento-dinero-page">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-3">
        <button onClick={() => navigate('/pos')} className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <Banknote className="w-5 h-5 text-zinc-600" />
          <h1 className="text-xl font-bold text-zinc-900">Movimiento de Dinero</h1>
        </div>
      </div>

      {/* Toggle */}
      <div className="px-4 py-3 border-b border-zinc-100">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => handleTipoChange('salida')}
            className={`h-12 rounded-xl font-medium text-base flex items-center justify-center gap-2 transition-all ${tipo === 'salida' ? 'bg-orange-600 text-white' : 'bg-zinc-100 text-zinc-600'}`}
            data-testid="btn-tipo-salida"
          >
            <ArrowUpFromLine className="w-5 h-5" />
            Enviar
          </button>
          <button
            onClick={() => handleTipoChange('entrada')}
            className={`h-12 rounded-xl font-medium text-base flex items-center justify-center gap-2 transition-all ${tipo === 'entrada' ? 'bg-green-600 text-white' : 'bg-zinc-100 text-zinc-600'}`}
            data-testid="btn-tipo-entrada"
          >
            <ArrowDownToLine className="w-5 h-5" />
            Recibir
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* ===== SALIDA / ENVIAR ===== */}
      {tipo === 'salida' && (
        <>
          <div className="flex-1 overflow-auto px-4 py-4 space-y-4">
            {/* Destino */}
            <div>
              <p className="text-xs text-zinc-500 font-medium mb-2 uppercase tracking-wide">Punto de venta destino</p>
              <div className="flex gap-2 flex-wrap">
                {puntosVenta.map(p => (
                  <button
                    key={p.id}
                    onClick={() => !p.cerrado && setDestino(p.id)}
                    disabled={p.cerrado}
                    title={p.cerrado ? 'Turno cerrado' : undefined}
                    className={`px-4 h-10 rounded-xl border text-sm font-medium transition-all
                      ${p.cerrado
                        ? 'border-zinc-200 bg-zinc-100 text-zinc-400 cursor-not-allowed'
                        : destino === p.id
                          ? 'border-zinc-900 bg-zinc-900 text-white'
                          : 'border-zinc-300 text-zinc-700'}`}
                  >
                    {p.name}{p.cerrado ? ' (cerrado)' : ''}
                  </button>
                ))}
              </div>
            </div>

            {/* Monto */}
            <div>
              <p className="text-xs text-zinc-500 font-medium mb-2 uppercase tracking-wide">Monto a enviar</p>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-zinc-400">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={monto}
                  onChange={e => setMonto(e.target.value)}
                  onFocus={e => e.target.select()}
                  placeholder="0.00"
                  className="w-full h-16 pl-10 pr-4 text-3xl font-bold border-2 border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-900 text-zinc-900 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  data-testid="input-monto"
                />
              </div>
            </div>

            {/* Nota */}
            <div>
              <p className="text-xs text-zinc-500 font-medium mb-2 uppercase tracking-wide">Nota (opcional)</p>
              <input
                type="text"
                value={nota}
                onChange={e => setNota(e.target.value)}
                placeholder="Ej. Pago de mercancía, fondo de caja..."
                className="w-full h-12 px-4 border border-zinc-200 rounded-xl text-sm text-zinc-900 focus:outline-none focus:border-zinc-500"
                data-testid="input-nota"
              />
            </div>

            {/* Historial salidas */}
            {historialSalidas.length > 0 && (
              <div>
                <p className="text-xs text-zinc-500 font-medium mb-3 uppercase tracking-wide flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Envíos de hoy
                </p>
                <div className="space-y-2">
                  {historialSalidas.map(s => {
                    const statusBadge = {
                      recibido:  { cls: 'bg-green-100 text-green-700',  label: 'Confirmado' },
                      cancelado: { cls: 'bg-zinc-100 text-zinc-400',    label: 'Cancelado' },
                    }[s.status] ?? { cls: 'bg-amber-100 text-amber-700', label: 'En tránsito' };
                    return (
                      <div key={s.id} className={`p-3 rounded-xl border ${s.status === 'cancelado' ? 'bg-zinc-50 border-zinc-100 opacity-60' : 'bg-zinc-50 border-zinc-200'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-zinc-900">{s.destino_nombre}</span>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge.cls}`}>
                              {statusBadge.label}
                            </span>
                            {s.status === 'en_transito' && cancelando !== s.id && (
                              <button
                                onClick={() => setCancelando(s.id)}
                                className="w-6 h-6 flex items-center justify-center rounded-full text-zinc-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                                title="Cancelar envío"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-base font-bold text-orange-600">{fmt(s.monto)}</p>
                        {s.monto_confirmado != null && s.monto_confirmado !== s.monto && (
                          <p className="text-xs text-green-600">Confirmado: {fmt(s.monto_confirmado)}</p>
                        )}
                        {s.nota && <p className="text-xs text-zinc-400 mt-0.5">{s.nota}</p>}
                        <p className="text-xs text-zinc-400 mt-0.5">{formatTime(s.created_at)}{s.confirmado_por_nombre ? ` · Recibido por ${s.confirmado_por_nombre}` : ''}</p>
                        {cancelando === s.id && (
                          <div className="mt-2 flex items-center gap-2 bg-red-50 rounded-lg px-3 py-2">
                            <span className="text-xs text-red-700 flex-1">¿Cancelar este envío?</span>
                            <button
                              onClick={() => handleCancelarEnvio(s.id)}
                              className="px-3 h-7 bg-red-600 text-white text-xs font-medium rounded-lg"
                            >
                              Sí, cancelar
                            </button>
                            <button
                              onClick={() => setCancelando(null)}
                              className="px-3 h-7 border border-zinc-300 text-xs font-medium rounded-lg"
                            >
                              No
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-zinc-200 px-4 py-4 bg-white">
            {destino && monto && parseFloat(monto) > 0 && (
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-zinc-500">Enviando a {puntosVenta.find(p => p.id === destino)?.name}</span>
                <span className="text-xl font-bold text-orange-600">{fmt(parseFloat(monto))}</span>
              </div>
            )}
            <button
              onClick={handleSubmitSalida}
              disabled={submitting || !destino || !monto || parseFloat(monto) <= 0}
              className="w-full h-14 bg-orange-600 text-white rounded-xl text-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 active:bg-orange-700"
              data-testid="btn-confirmar-salida"
            >
              {submitting
                ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <><Truck className="w-5 h-5" />Registrar envío</>}
            </button>
          </div>
        </>
      )}

      {/* ===== ENTRADA / RECIBIR ===== */}
      {tipo === 'entrada' && !seleccionado && (
        <div className="flex-1 overflow-auto px-4 py-4">
          {pendientes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-zinc-400">
              <Banknote className="w-16 h-16 mb-4" />
              <p className="text-lg font-medium">Sin envíos pendientes</p>
              <p className="text-sm mt-1">No hay dinero en tránsito hacia este punto</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-zinc-500 font-medium mb-3 uppercase tracking-wide">Dinero pendiente de recibir</p>
              <div className="space-y-3">
                {pendientes.map(t => (
                  <button
                    key={t.id}
                    onClick={() => selectPendiente(t)}
                    className="w-full p-4 bg-white border-2 border-zinc-200 rounded-xl text-left hover:border-green-300 active:bg-zinc-50 transition-all"
                    data-testid={`pendiente-${t.id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Banknote className="w-4 h-4 text-amber-500" />
                        <span className="font-bold text-zinc-900">{t.origen_nombre}</span>
                      </div>
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">En tránsito</span>
                    </div>
                    <p className="text-2xl font-bold text-zinc-900">{fmt(t.monto)}</p>
                    {t.nota && <p className="text-xs text-zinc-500 mt-1">{t.nota}</p>}
                    <p className="text-xs text-zinc-400 mt-1">Enviado por {t.creado_por_nombre} · {formatTime(t.created_at)}</p>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Historial entradas */}
          {historialEntradas.length > 0 && (
            <div className="mt-6">
              <p className="text-xs text-zinc-500 font-medium mb-3 uppercase tracking-wide flex items-center gap-1">
                <Clock className="w-3 h-3" /> Entradas confirmadas hoy
              </p>
              <div className="space-y-2">
                {historialEntradas.map(e => (
                  <div key={e.id} className="p-3 bg-zinc-50 rounded-xl border border-zinc-200">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-zinc-900">{e.origen_nombre}</span>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">Recibido</span>
                    </div>
                    <p className="text-base font-bold text-green-600">{fmt(e.monto_confirmado)}</p>
                    {e.monto_confirmado !== e.monto && (
                      <p className="text-xs text-zinc-400">Enviado: {fmt(e.monto)}</p>
                    )}
                    {e.nota && <p className="text-xs text-zinc-400 mt-0.5">{e.nota}</p>}
                    <p className="text-xs text-zinc-400 mt-0.5">{formatTime(e.confirmed_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== CONFIRMAR ENTRADA ===== */}
      {tipo === 'entrada' && seleccionado && (
        <>
          <div className="px-4 pt-4 pb-2 flex items-center gap-2 bg-green-50 border-b border-green-100">
            <button onClick={() => { setSeleccionado(null); setRechazando(false); setMotivoRechazo(''); setError(''); }} className="text-sm text-green-700 font-medium">← Volver</button>
            <span className="text-sm text-zinc-600">Confirmar recepción de <strong>{seleccionado.origen_nombre}</strong></span>
          </div>

          <div className="flex-1 overflow-auto px-4 py-4 space-y-4">
            <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-xl">
              <p className="text-xs text-zinc-500 mb-1">Monto enviado</p>
              <p className="text-3xl font-bold text-zinc-900">{fmt(seleccionado.monto)}</p>
              {seleccionado.nota && <p className="text-sm text-zinc-500 mt-1">{seleccionado.nota}</p>}
              <p className="text-xs text-zinc-400 mt-1">Enviado por {seleccionado.creado_por_nombre}</p>
            </div>

            <div>
              <p className="text-xs text-zinc-500 font-medium mb-2 uppercase tracking-wide">Monto realmente recibido</p>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-zinc-400">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={montoConfirmado}
                  onChange={e => setMontoConfirmado(e.target.value)}
                  onFocus={e => e.target.select()}
                  className="w-full h-16 pl-10 pr-4 text-3xl font-bold border-2 border-zinc-200 rounded-xl focus:outline-none focus:border-green-500 text-zinc-900 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  data-testid="input-monto-confirmado"
                />
              </div>
              <p className="text-xs text-zinc-400 mt-2">Ajusta si recibiste un monto diferente al enviado.</p>
            </div>
          </div>

          <div className="border-t border-zinc-200 px-4 py-4 bg-white">
            <button
              onClick={handleConfirmarEntrada}
              disabled={submitting || montoConfirmado === '' || parseFloat(montoConfirmado) < 0}
              className="w-full h-14 bg-green-600 text-white rounded-xl text-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 active:bg-green-700"
              data-testid="btn-confirmar-entrada"
            >
              {submitting
                ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <><Check className="w-5 h-5" />Confirmar recepción</>}
            </button>
            {!rechazando ? (
              <button
                onClick={() => { setRechazando(true); setError(''); }}
                disabled={submitting}
                className="w-full h-12 mt-2 border border-red-300 text-red-600 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="btn-rechazar-entrada-dinero"
              >
                <Ban className="w-5 h-5" />No recibí este dinero: rechazar
              </button>
            ) : (
              <div className="mt-3 p-3 border border-red-200 bg-red-50 rounded-xl space-y-2">
                <p className="text-sm font-medium text-red-800">¿Por qué rechazas este envío?</p>
                <textarea
                  value={motivoRechazo}
                  onChange={e => setMotivoRechazo(e.target.value)}
                  rows={2}
                  placeholder="Ej. no me entregaron el dinero, el monto no corresponde..."
                  className="w-full p-2 text-sm border border-red-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-400 bg-white"
                  data-testid="input-motivo-rechazo-dinero"
                />
                <p className="text-xs text-red-700">No entra a tu caja. Quedará en la bitácora y en tu corte.</p>
                <div className="flex gap-2">
                  <button onClick={() => { setRechazando(false); setMotivoRechazo(''); }} className="flex-1 h-11 border border-zinc-300 rounded-lg text-sm font-medium bg-white">
                    Cancelar
                  </button>
                  <button
                    onClick={handleRechazarEntrada}
                    disabled={submitting || motivoRechazo.trim().length < 3}
                    className="flex-1 h-11 bg-red-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
                    data-testid="btn-confirmar-rechazo-dinero"
                  >
                    Rechazar envío
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
